import {
  Codec,
  array,
  string,
  number,
  GetInterface,
  exactly,
  oneOf,
  boolean,
} from "purify-ts/Codec";
import { Either, Left } from "purify-ts/Either";
import { RemoteData } from "@nvdb-rapport/shared/src/utils/remoteData";
import axios from "axios";

import { EitherAsync } from "purify-ts";
import qs from "qs";
import {
  Omraader,
  transformOmraader,
} from "@nvdb-rapport/shared/src/components/AreaSelect/types";

const slug = "kostra";
const rapportHeaders = {
  "X-client": "NVDB-Rapporter-Frontend-Gammel",
};

const myHost = typeof window !== "undefined" ? window.location.href : "";
function contextualPath(): string {
  if (myHost.includes("localhost")) {
    return "http://localhost:8080/rapporter"; //"https://nvdbrapportapi.atlas.vegvesen.no/rapporter";
  } else if (myHost.includes(`${slug}.utv.atlas.vegvesen.no`)) {
    return "https://nvdbrapportapi.utv.atlas.vegvesen.no/rapporter";
  } else if (myHost.includes(`${slug}.test.atlas.vegvesen.no`)) {
    return "https://nvdbrapportapi.test.atlas.vegvesen.no/rapporter";
  } else if (myHost.includes(`${slug}.atlas.vegvesen.no`)) {
    return "https://nvdbrapportapi.atlas.vegvesen.no/rapporter";
  }
  return "http://localhost:8080/rapporter";
}
export const apiPath = contextualPath();

export async function getOmraader(): Promise<RemoteData<Error, Omraader>> {
  try {
    const result = await axios.get(`${apiPath}/omraader`, {
      headers: rapportHeaders,
    });
    const decodedOmraade = Omraader.decode(result.data);
    if (result.status !== 200) throw "Server error";
    return decodedOmraade.caseOf({
      Left: error =>
        ({
          type: "FAILURE",
          error: { message: error },
        } as RemoteData<Error, Omraader>),
      Right: data =>
        ({
          type: "SUCCESS",
          data: transformOmraader(data),
        } as RemoteData<Error, Omraader>),
    });
  } catch (error) {
    return { type: "FAILURE", error };
  }
}

export async function getKostrarapporttyper(): Promise<
  RemoteData<Error, KostraRapportTypeList>
> {
  try {
    const result = await axios.get(`${apiPath}/kostrarapporttyper`, {
      headers: rapportHeaders,
    });
    const decodedOmraade = KostraRapportTypeList.decode(result.data);
    if (result.status !== 200) throw "Server error";
    return decodedOmraade.caseOf({
      Left: error =>
        ({
          type: "FAILURE",
          error: { message: error },
        } as RemoteData<Error, KostraRapportTypeList>),
      Right: data =>
        ({
          type: "SUCCESS",
          data,
        } as RemoteData<Error, KostraRapportTypeList>),
    });
  } catch (error) {
    return { type: "FAILURE", error };
  }
}

const KostraRapportType = Codec.interface({
  id: number,
  navn: string,
  implementert: boolean,
});

export type KostraRapportType = GetInterface<typeof KostraRapportType>;

const KostraRapportTypeList = array(KostraRapportType);

export type KostraRapportTypeList = GetInterface<typeof KostraRapportTypeList>;

// FETCHING THE CONTRACT

const Rapport = Codec.interface({
  nedlastLink: string,
  filtype: exactly("XLSX"),
  navn: string,
  id: number,
});

export type Rapport = GetInterface<typeof Rapport>;

export const ErrorPoll = Codec.interface({
  id: string,
  durationMs: number,
  durationMsString: string,
  status: exactly("ERROR"),
  error: string,
});

export type ErrorPoll = GetInterface<typeof ErrorPoll>;

export const PendingPoll = Codec.interface({
  id: string,
  durationMs: number,
  durationMsString: string,
  status: exactly("PENDING"),
});

export type PendingPoll = GetInterface<typeof PendingPoll>;

export const SuccessPoll = Codec.interface({
  id: string,
  durationMs: number,
  durationMsString: string,
  status: exactly("SUCCESS"),
  rapport: Rapport,
});

export type SuccessPoll = GetInterface<typeof SuccessPoll>;

export const UnknownPoll = Codec.interface({
  id: string,
  status: exactly("UNKNOWN"),
});

export type UnknownPoll = GetInterface<typeof UnknownPoll>;

export const PollData = oneOf([
  ErrorPoll,
  PendingPoll,
  SuccessPoll,
  UnknownPoll,
]);

export type PollData = GetInterface<typeof PollData>;

export type QueryData = [
  number,
  {
    kommune: number[] | undefined;
    fylke: number[] | undefined;
    V4?: boolean;
  }
];

export async function requestReport(
  query: QueryData
): Promise<Either<string, PollData>> {
  try {
    const response = await axios.get(`${apiPath}/kostra/${query[0]}`, {
      headers: rapportHeaders,
      params: query[1],
      paramsSerializer: params =>
        qs.stringify(params, { arrayFormat: "repeat" }),
    });

    return PollData.decode(response.data);
  } catch (e) {
    switch (e.response.status) {
      case 400:
        return Left(e.response.data.error);
      default:
        return Left(
          `Nettverksfeil ${e.response.status}: ${e.response.data.error}`
        );
    }
  }
}

export async function pollReport(
  id: string
): Promise<Either<string, PollData>> {
  try {
    const response = await axios.get(`${apiPath}/poll/${id}`, {
      headers: rapportHeaders,
    });

    return PollData.decode(response.data);
  } catch (e) {
    return Left(e);
  }
}

export function downloadStatisticsLink(
  filnavn: string,
  filtype: "XLSX"
): string {
  return `${apiPath}/nedlasting/${filnavn}?filtype=${filtype}`;
}

export function getPollResult(pollId: string) {
  return EitherAsync<string, PollData>(async ({ liftEither, throwE }) => {
    const poll = await pollReport(pollId);
    const pollResult = await liftEither(poll);
    return pollResult;
  });
}
